import { useState, type ReactNode } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Check, Download, FileText, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { FormPageLayout } from '@/components/layouts';
import { useChangeOrder, useJobScopeHistory } from '@/hooks/change-orders/useChangeOrders';
import { useJob } from '@/hooks/jobs/useJob';
import { useCompanySettings } from '@/hooks/settings/useCompanySettings';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useUsers } from '@/hooks/settings/useUsers';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { downloadChangeOrderPdf } from '@/lib/change-orders/downloadChangeOrderPdf';
import { cn } from '@/lib/utils';
import type {
  ChangeOrder,
  ChangeOrderLineItem,
  ChangeOrderStatus,
  JobScopeHistory,
  LineItemModification,
} from '@/types/changeOrder';
import type { TeamUser } from '@/types/settings';
import { ChangeOrderFormPage } from './$id.change-orders.new';

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id/change-orders/$changeOrderId')(
  {
    component: ChangeOrderRoutePage,
  },
);

type DisplayStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'DECLINED' | 'VOID';

const STATUS_LABEL: Record<DisplayStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  VOID: 'Void',
};

const DETAIL_FIELDS: Array<keyof ChangeOrderLineItem> = [
  'name',
  'type',
  'quantity',
  'manualPriceOverride',
  'basePrice',
  'laborRate',
  'priceStrategy',
  'taxable',
  'description',
];

const PRICING_FIELDS: Array<keyof ChangeOrderLineItem> = [
  'quantity',
  'manualPriceOverride',
  'basePrice',
  'laborRate',
  'priceStrategy',
  'discountAmount',
  'discountPercent',
  'taxable',
  'lineSubtotal',
  'lineTax',
  'lineTotal',
];

type ScopeItem = Partial<ChangeOrderLineItem> & Record<string, unknown>;

function ChangeOrderRoutePage() {
  const { id, changeOrderId } = Route.useParams();
  const router = useRouter();
  const { data: changeOrder, isLoading } = useChangeOrder(changeOrderId);
  const navigateBack = () =>
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: { tab: 'finances' as const },
    });

  if (isLoading) {
    return (
      <FormPageLayout title="Change order details" onBack={navigateBack} noscroll>
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading change order...
        </div>
      </FormPageLayout>
    );
  }

  if (changeOrder?.status === 'DRAFT') {
    return <ChangeOrderFormPage jobId={id} mode="edit" changeOrderId={changeOrderId} />;
  }

  return <ChangeOrderDetailPage jobId={id} changeOrderId={changeOrderId} />;
}

function ChangeOrderDetailPage({ jobId, changeOrderId }: { jobId: string; changeOrderId: string }) {
  const router = useRouter();
  const { data: job, isLoading: isJobLoading } = useJob(jobId);
  const { data: changeOrder, isLoading: isChangeOrderLoading } = useChangeOrder(changeOrderId);
  const { data: scopeHistory } = useJobScopeHistory(jobId);
  const { data: usersData } = useUsers();
  const { data: companySettings } = useCompanySettings();
  const companyTimeZone = useCompanyTimezone();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const navigateBack = () =>
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id: jobId },
      search: { tab: 'finances' as const },
    });

  if (isJobLoading || isChangeOrderLoading) {
    return (
      <FormPageLayout title="Change order details" onBack={navigateBack} noscroll>
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading change order...
        </div>
      </FormPageLayout>
    );
  }

  if (!changeOrder) {
    return (
      <FormPageLayout title="Change order details" onBack={navigateBack} noscroll>
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Change order not found.
          </div>
        </div>
      </FormPageLayout>
    );
  }

  const jobTotal = Number(job?.grandTotal ?? 0);
  const status = displayStatus(changeOrder.status);
  const users = usersData?.data ?? [];
  const scopeItems = buildScopeItems(scopeHistory, job, changeOrder);
  const removedSnapshots = asArray(changeOrder.lineItemsRemovedSnapshot);
  const removedItems = asArray(changeOrder.lineItemsRemoved).map((lineItemId) => ({
    lineItemId,
    item:
      findScopeItem(removedSnapshots as ScopeItem[], lineItemId) ??
      findScopeItem(scopeItems, lineItemId),
  }));
  const addedTotal = sumLineItems(asArray(changeOrder.lineItemsAdded));
  const removedTotal = removedItems.reduce((sum, entry) => sum + lineItemTotal(entry.item), 0);
  const modifiedRows = asArray(changeOrder.lineItemsModified).map((modification) =>
    buildModifiedRow(modification, scopeItems),
  );
  const modifiedDelta = modifiedRows.reduce((sum, row) => sum + row.delta, 0);
  const newJobTotal =
    changeOrder.status === 'APPLIED' ? jobTotal : jobTotal + changeOrder.priceImpact.totalDelta;
  const subtitle = [
    job?.jobNumber,
    resolveJobHeaderName(job),
    STATUS_LABEL[status],
    `net impact ${formatSignedCurrency(changeOrder.priceImpact.totalDelta)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  async function handleDownloadPdf() {
    if (isDownloadingPdf) return;
    if (!changeOrder) return;
    setIsDownloadingPdf(true);
    try {
      await downloadChangeOrderPdf({
        changeOrder,
        job,
        scopeHistory,
        users: users,
        company: companySettings,
        timeZone: companyTimeZone,
      });
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  // NOT-367 — copy the public approval link (mirrors the estimate "Copy link").
  function handleCopyLink() {
    if (!changeOrder) return;
    const link = `${window.location.origin}/change-order/${changeOrder.id}`;
    void navigator.clipboard.writeText(link).then(
      () => toast.success('Change order link copied'),
      () => toast.error('Failed to copy link'),
    );
  }

  return (
    <FormPageLayout
      title={`Change order ${coLabel(changeOrder)}`}
      subtitle={subtitle}
      onBack={navigateBack}
      actions={
        changeOrder.status === 'PENDING_APPROVAL' ? (
          <Button type="button" variant="outline" size="sm" onClick={handleCopyLink}>
            <Link2 className="size-4" />
            Copy link
          </Button>
        ) : changeOrder.status === 'APPROVED' || changeOrder.status === 'APPLIED' ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDownloadingPdf}
            onClick={() => void handleDownloadPdf()}
          >
            {isDownloadingPdf ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
            Download PDF
          </Button>
        ) : null
      }
      noscroll
    >
      <div className="h-full overflow-y-auto">
        <div className="mx-auto grid max-w-[1120px] gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,760px)_300px]">
          <div className="space-y-4">
            <SectionCard>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={status} />
                    <span className="text-sm text-muted-foreground">
                      Created {formatDate(changeOrder.createdAt, companyTimeZone)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Reason
                    </p>
                    <p className="mt-1 text-base font-medium text-foreground">
                      {changeOrder.reason || changeOrder.description || 'No reason provided'}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-muted/70 px-3 py-2 text-sm">
                  <p className="text-muted-foreground">Current status</p>
                  <p className="font-semibold text-foreground">{STATUS_LABEL[status]}</p>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Scope changes">
              <div className="mt-4 space-y-5">
                <ChangeGroup title="Added items" count={changeOrder.lineItemsAdded.length}>
                  {changeOrder.lineItemsAdded.length > 0 ? (
                    changeOrder.lineItemsAdded.map((item) => (
                      <LineItemDetailRow
                        key={item.lineItemId}
                        title={item.name || 'Item'}
                        meta={`${item.type || 'Item'} · ${formatQuantity(item)}`}
                        amount={lineItemTotal(item)}
                        tag={<ChangeTag tone="success">New</ChangeTag>}
                      />
                    ))
                  ) : (
                    <EmptyLine>No added items.</EmptyLine>
                  )}
                </ChangeGroup>

                <ChangeGroup title="Removed items" count={removedItems.length}>
                  {removedItems.length > 0 ? (
                    removedItems.map(({ lineItemId, item }) => (
                      <LineItemDetailRow
                        key={lineItemId}
                        title={lineItemDisplayName(item)}
                        meta={
                          item ? `${item.type || 'Item'} · ${formatQuantity(item)}` : 'Removed item'
                        }
                        amount={-lineItemTotal(item)}
                        tag={<ChangeTag tone="danger">Removing</ChangeTag>}
                        muted
                      />
                    ))
                  ) : (
                    <EmptyLine>No removed items.</EmptyLine>
                  )}
                </ChangeGroup>

                <ChangeGroup title="Modified items" count={modifiedRows.length}>
                  {modifiedRows.length > 0 ? (
                    modifiedRows.map((row) => (
                      <div key={row.lineItemId} className="rounded-lg border px-3 py-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-foreground">{row.name}</p>
                              <ChangeTag tone="warning">Modifying</ChangeTag>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {formatCurrency(row.beforeTotal)} → {formatCurrency(row.afterTotal)}
                            </p>
                          </div>
                          <p
                            className={cn(
                              'font-semibold tabular-nums',
                              row.delta > 0 && 'text-emerald-700',
                              row.delta < 0 && 'text-destructive',
                              row.delta === 0 && 'text-muted-foreground',
                            )}
                          >
                            {formatSignedCurrency(row.delta)}
                          </p>
                        </div>
                        <div className="mt-3 space-y-1.5">
                          {row.fields.length > 0 ? (
                            row.fields.map((field) => (
                              <p key={field.label} className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">{field.label}:</span>{' '}
                                {formatFieldTransition(field.before, field.after)}
                              </p>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No field-level details were captured.
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyLine>No modified items.</EmptyLine>
                  )}
                </ChangeGroup>
              </div>
            </SectionCard>

            <SectionCard title="Approval history">
              <ApprovalHistory
                changeOrder={changeOrder}
                status={status}
                users={users}
                timeZone={companyTimeZone}
              />
            </SectionCard>
          </div>

          <aside className="lg:sticky lg:top-4 lg:self-start">
            <section className="rounded-lg bg-muted/70 p-4 shadow-xs">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Financial impact
              </h2>
              <div className="mt-5 space-y-4 text-sm">
                <ImpactRow label="Added" value={addedTotal} tone="success" />
                <ImpactRow label="Removed" value={-removedTotal} tone="danger" />
                <ImpactRow label="Modified" value={modifiedDelta} />
                <div className="border-t pt-4">
                  <ImpactRow
                    label="Net impact"
                    value={changeOrder.priceImpact.totalDelta}
                    prominent
                  />
                </div>
                <ImpactRow label="Tax impact" value={changeOrder.priceImpact.taxDelta} />
                <ImpactRow label="New job total" value={newJobTotal} total />
              </div>
            </section>
          </aside>
        </div>
      </div>
    </FormPageLayout>
  );
}

function SectionCard({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-background p-4 shadow-xs">
      {title ? <h2 className="text-base font-semibold text-foreground">{title}</h2> : null}
      {children}
    </section>
  );
}

function ChangeGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </p>
        <Badge variant="secondary" className="rounded-full">
          {count}
        </Badge>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function LineItemDetailRow({
  title,
  meta,
  amount,
  tag,
  muted,
}: {
  title: string;
  meta: string;
  amount: number;
  tag: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className={cn('font-semibold text-foreground', muted && 'line-through opacity-70')}>
            {title}
          </p>
          {tag}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{meta}</p>
      </div>
      <p
        className={cn(
          'font-semibold tabular-nums text-foreground',
          amount > 0 && 'text-emerald-700',
          amount < 0 && 'text-destructive',
        )}
      >
        {formatSignedCurrency(amount)}
      </p>
    </div>
  );
}

function EmptyLine({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-md bg-muted/50 px-3 py-3 text-sm text-muted-foreground">{children}</p>
  );
}

function ChangeTag({
  children,
  tone,
}: {
  children: ReactNode;
  tone: 'success' | 'warning' | 'danger';
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-semibold',
        tone === 'success' && 'bg-emerald-100 text-emerald-700',
        tone === 'warning' && 'bg-amber-100 text-amber-700',
        tone === 'danger' && 'bg-destructive/10 text-destructive',
      )}
    >
      {children}
    </span>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium text-foreground">{value}</p>
    </div>
  );
}

function ApprovalHistory({
  changeOrder,
  status,
  users,
  timeZone,
}: {
  changeOrder: ChangeOrder;
  status: DisplayStatus;
  users: TeamUser[];
  timeZone?: string | null;
}) {
  const requestedBy = formatActorName(changeOrder.requestedBy, users, changeOrder.requestedByName);
  const approvedBy = formatActorName(changeOrder.approvedBy, users, changeOrder.approvedByName);
  const rejectedBy = formatActorName(changeOrder.rejectedBy, users, changeOrder.rejectedByName);

  return (
    <div className="mt-4 space-y-4 text-sm">
      <div className="grid gap-3 sm:grid-cols-2">
        <DetailItem label="Requested by" value={requestedBy} />
        <DetailItem label="Requested at" value={formatDate(changeOrder.createdAt, timeZone)} />
      </div>

      {status === 'PENDING_APPROVAL' ? (
        <div className="rounded-md bg-amber-50 px-3 py-3 text-sm font-medium text-amber-800">
          Waiting for customer response.
        </div>
      ) : null}

      {status === 'APPROVED' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {changeOrder.approvedBy || changeOrder.approvedByName ? (
            <DetailItem label="Approved by" value={approvedBy} />
          ) : null}
          {changeOrder.approvedAt ? (
            <DetailItem label="Approved at" value={formatDate(changeOrder.approvedAt, timeZone)} />
          ) : null}
          {changeOrder.appliedAt ? (
            <DetailItem label="Applied at" value={formatDate(changeOrder.appliedAt, timeZone)} />
          ) : null}
        </div>
      ) : null}

      {status === 'DECLINED' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {changeOrder.rejectedBy || changeOrder.rejectedByName ? (
            <DetailItem label="Declined by" value={rejectedBy} />
          ) : null}
          {changeOrder.rejectedAt ? (
            <DetailItem label="Declined at" value={formatDate(changeOrder.rejectedAt, timeZone)} />
          ) : null}
          {changeOrder.rejectionReason ? (
            <div className="sm:col-span-2">
              <DetailItem label="Decline reason" value={changeOrder.rejectionReason} />
            </div>
          ) : null}
        </div>
      ) : null}

      {status === 'VOID' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem label="Voided at" value={formatDate(changeOrder.updatedAt, timeZone)} />
        </div>
      ) : null}
    </div>
  );
}

function ImpactRow({
  label,
  value,
  tone,
  prominent,
  total,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'danger' | 'neutral';
  prominent?: boolean;
  total?: boolean;
}) {
  const computedTone = tone ?? (value > 0 ? 'success' : value < 0 ? 'danger' : 'neutral');
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span
        className={cn(
          'font-medium text-muted-foreground',
          (prominent || total) && 'text-foreground',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'font-semibold tabular-nums text-foreground',
          computedTone === 'success' && value !== 0 && 'text-emerald-700',
          computedTone === 'danger' && value !== 0 && 'text-destructive',
          prominent && 'text-base',
          total && 'text-base',
        )}
      >
        {value === 0 && !total ? '—' : total ? formatCurrency(value) : formatSignedCurrency(value)}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: DisplayStatus }) {
  const Icon = status === 'APPROVED' ? Check : FileText;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        status === 'PENDING_APPROVAL' && 'bg-amber-100 text-amber-700',
        status === 'APPROVED' && 'bg-emerald-100 text-emerald-700',
        (status === 'DRAFT' || status === 'DECLINED' || status === 'VOID') &&
          'bg-muted text-muted-foreground',
      )}
    >
      <Icon className="size-3" />
      {STATUS_LABEL[status]}
    </span>
  );
}

function coLabel(changeOrder: ChangeOrder): string {
  return `CO-${String(changeOrder.changeOrderNumber).padStart(4, '0')}`;
}

function displayStatus(status: ChangeOrderStatus): DisplayStatus {
  if (status === 'REJECTED') return 'DECLINED';
  if (status === 'VOIDED') return 'VOID';
  if (status === 'APPLIED') return 'APPROVED';
  return status;
}

function resolveJobHeaderName(
  job: { jobNumber?: string; name?: string; jobType?: string | { name?: string } } | undefined,
): string {
  if (!job) return 'Job';

  const name = cleanString(job.name);
  const jobNumber = cleanString(job.jobNumber);
  const jobTypeName = typeof job.jobType === 'string' ? job.jobType : job.jobType?.name;
  const generatedName = jobNumber ? `Job ${jobNumber}` : '';

  if (!name || name === jobNumber || name.toLowerCase() === generatedName.toLowerCase()) {
    return cleanString(jobTypeName) || 'Job';
  }

  if (jobNumber) {
    const nameWithoutGeneratedPrefix = name
      .replace(new RegExp(`^job\\s+${escapeRegExp(jobNumber)}\\s*(?:[-:]\\s*)?`, 'i'), '')
      .trim();

    if (nameWithoutGeneratedPrefix) return nameWithoutGeneratedPrefix;
  }

  return name;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatSignedCurrency(value: number): string {
  const formatted = formatCurrency(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

function formatDate(iso?: string | null, timeZone?: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return formatDateTimeInTimeZone(iso, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function asNumber(value: unknown, fallback = 0): number {
  const number = Number(value ?? fallback);
  return Number.isFinite(number) ? number : fallback;
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function userDisplayName(user: TeamUser): string {
  return (
    user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
  );
}

function formatActorName(
  idOrName: string | null | undefined,
  users: TeamUser[],
  explicitName?: string | null,
): string {
  const name = cleanString(explicitName);
  if (name && !isUuid(name)) return name;

  const value = cleanString(idOrName);
  if (!value || value.toLowerCase() === 'unknown') return 'Unknown user';

  const user = users.find(
    (candidate) =>
      candidate.id === value || candidate.membershipId === value || candidate.email === value,
  );
  if (user) return userDisplayName(user);

  return isUuid(value) ? 'Unknown user' : value;
}

function buildScopeItems(
  scopeHistory: JobScopeHistory | undefined,
  job: { lineItems?: unknown[] } | undefined,
  changeOrder?: ChangeOrder,
): ScopeItem[] {
  const scopeHistoryChangeOrders = Array.isArray(scopeHistory?.changeOrders)
    ? scopeHistory.changeOrders
    : [];

  return [
    ...(asArray(scopeHistory?.originalLineItems) as ScopeItem[]),
    ...(asArray(scopeHistory?.currentLineItems) as ScopeItem[]),
    ...(scopeHistoryChangeOrders.flatMap((entry) => [
      ...asArray(entry.lineItemsAdded),
      ...asArray(entry.lineItemsRemovedSnapshot),
      ...asArray(entry.lineItemsModified).flatMap((modification) =>
        modification.original ? [modification.original] : [],
      ),
    ]) as ScopeItem[]),
    ...(asArray(changeOrder?.lineItemsRemovedSnapshot) as ScopeItem[]),
    ...(asArray(changeOrder?.lineItemsModified).flatMap((modification) =>
      modification.original ? [modification.original] : [],
    ) as ScopeItem[]),
    ...((Array.isArray(job?.lineItems) ? job.lineItems : []) as ScopeItem[]),
  ];
}

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function lineItemIds(item: ScopeItem | Partial<ChangeOrderLineItem> | undefined): string[] {
  if (!item) return [];
  const raw = item as Record<string, unknown>;
  return [
    cleanString(raw.lineItemId),
    cleanString(raw.itemId),
    cleanString(raw.snapshotId),
    cleanString(raw.id),
  ].filter(Boolean);
}

function lineItemDisplayName(item: Partial<ChangeOrderLineItem> | undefined): string {
  if (!item) return 'Line item';
  const name = cleanString(item.name);
  if (name && !isUuid(name)) return name;
  const description = cleanString(item.description);
  if (description && !isUuid(description)) return description;
  const code = cleanString(item.code);
  if (code && !isUuid(code)) return code;
  return 'Line item';
}

function lineItemTotal(item?: Partial<ChangeOrderLineItem>): number {
  if (!item) return 0;
  const quantity = asNumber(item.quantity, 1);
  const unitPrice = asNumber(
    item.manualPriceOverride ?? item.basePrice ?? item.laborRate ?? item.lineSubtotal,
    0,
  );
  return asNumber(item.lineTotal, quantity * unitPrice);
}

function shouldRecalculateModifiedTotal(updated: Partial<ChangeOrderLineItem>): boolean {
  return PRICING_FIELDS.some((field) => field in updated) && !('lineTotal' in updated);
}

function calculateLineTotalFromPricing(
  item: Partial<ChangeOrderLineItem>,
  effectiveTaxRate = 0,
): number {
  const quantity = asNumber(item.quantity, 1);
  const strategy = cleanString(item.priceStrategy).toUpperCase();
  const manualPriceOverride = item.manualPriceOverride;
  let unitPrice: number;

  if (manualPriceOverride != null) {
    unitPrice = asNumber(manualPriceOverride);
  } else if (
    strategy.includes('TIME') ||
    strategy.includes('T&M') ||
    strategy.includes('T_AND_M')
  ) {
    unitPrice = asNumber(item.basePrice) + asNumber(item.laborRate);
  } else {
    unitPrice = asNumber(item.basePrice ?? item.laborRate ?? item.lineSubtotal);
  }

  const lineSubtotal = quantity * unitPrice;
  const discountPercent = asNumber(item.discountPercent);
  const discountAmount = asNumber(item.discountAmount);
  const percentDiscount = discountPercent > 0 ? (lineSubtotal * discountPercent) / 100 : 0;
  const discountSavings = Math.min(lineSubtotal, percentDiscount + Math.max(0, discountAmount));
  const afterDiscount = lineSubtotal - discountSavings;
  const lineTax = item.taxable ? afterDiscount * effectiveTaxRate : 0;

  return Math.round((afterDiscount + lineTax) * 100) / 100;
}

function deriveEffectiveTaxRate(item: Partial<ChangeOrderLineItem>): number {
  if (!item.taxable) return 0;
  const lineSubtotal = asNumber(item.lineSubtotal);
  const discountPercent = asNumber(item.discountPercent);
  const discountAmount = asNumber(item.discountAmount);
  const percentDiscount = discountPercent > 0 ? (lineSubtotal * discountPercent) / 100 : 0;
  const taxableBase = lineSubtotal - Math.min(lineSubtotal, percentDiscount + discountAmount);
  const lineTax = asNumber(item.lineTax);

  if (taxableBase <= 0 || lineTax <= 0) return 0;
  return lineTax / taxableBase;
}

function sumLineItems(items: ChangeOrderLineItem[]): number {
  return items.reduce((sum, item) => sum + lineItemTotal(item), 0);
}

function formatQuantity(item: Partial<ChangeOrderLineItem>): string {
  const quantity = asNumber(item.quantity, 1);
  const unit = item.priceStrategy?.toUpperCase().includes('TIME') ? 'h' : item.unit || 'u';
  return `${quantity} ${unit}`;
}

function findScopeItem(items: ScopeItem[] | undefined, lineItemId: string): ScopeItem | undefined {
  return items?.find((item) => lineItemIds(item).includes(lineItemId));
}

function mergeLineItem(
  lineItemId: string,
  original?: Partial<ChangeOrderLineItem>,
  updated?: Partial<ChangeOrderLineItem>,
): Partial<ChangeOrderLineItem> {
  return {
    lineItemId,
    name: original?.name ?? updated?.name ?? lineItemId,
    type: original?.type ?? updated?.type ?? 'Item',
    quantity: original?.quantity ?? updated?.quantity ?? 1,
    ...original,
    ...updated,
  };
}

function buildModifiedRow(modification: LineItemModification, scopeItems: ScopeItem[] | undefined) {
  const fallbackOriginal = findScopeItem(scopeItems, modification.lineItemId);
  const before = mergeLineItem(modification.lineItemId, fallbackOriginal, modification.original);
  const after = mergeLineItem(modification.lineItemId, before, modification.updated);
  const beforeTotal = lineItemTotal(before);
  const afterTotal = shouldRecalculateModifiedTotal(modification.updated)
    ? calculateLineTotalFromPricing(after, deriveEffectiveTaxRate(before))
    : lineItemTotal(after);

  return {
    lineItemId: modification.lineItemId,
    name: lineItemDisplayName(after),
    beforeTotal,
    afterTotal,
    delta: afterTotal - beforeTotal,
    fields: changedFields(before, modification.updated),
  };
}

function changedFields(
  before: Partial<ChangeOrderLineItem>,
  updated: Partial<ChangeOrderLineItem>,
) {
  return DETAIL_FIELDS.flatMap((field) => {
    if (!(field in updated)) return [];
    if (
      field === 'manualPriceOverride' &&
      'basePrice' in updated &&
      String(before.basePrice ?? '') !== String(updated.basePrice ?? '')
    ) {
      return [];
    }

    const beforeValue = before[field];
    const afterValue = updated[field];
    if (String(beforeValue ?? '') === String(afterValue ?? '')) return [];
    return [
      {
        label: fieldLabel(field),
        before: formatFieldValue(field, beforeValue),
        after: formatFieldValue(field, afterValue),
      },
    ];
  });
}

function fieldLabel(field: keyof ChangeOrderLineItem): string {
  const labels: Partial<Record<keyof ChangeOrderLineItem, string>> = {
    name: 'Item name',
    type: 'Type',
    quantity: 'Quantity',
    manualPriceOverride: 'Price',
    basePrice: 'Price',
    laborRate: 'Hourly rate',
    priceStrategy: 'Billing',
    taxable: 'Tax',
    description: 'Description',
  };

  return labels[field] ?? String(field);
}

function formatFieldTransition(before: string, after: string): string {
  if (!before) return `→ ${after}`;
  return `${before} → ${after}`;
}

function formatFieldValue(field: keyof ChangeOrderLineItem, value: unknown): string {
  if (value == null || value === '') return '';
  if (
    field === 'manualPriceOverride' ||
    field === 'basePrice' ||
    field === 'laborRate' ||
    field === 'lineTotal'
  ) {
    return formatCurrency(asNumber(value));
  }
  if (field === 'priceStrategy') return formatBillingValue(value);
  if (field === 'type') return formatTitleValue(value);
  if (field === 'taxable') return value ? 'Taxable' : 'Exempt';
  return String(value);
}

function formatBillingValue(value: unknown): string {
  const raw = cleanString(value).toUpperCase();
  if (!raw) return '';
  if (raw.includes('TIME') || raw.includes('T&M') || raw.includes('T_AND_M')) return 'T&M';
  if (raw === 'FLAT' || raw === 'FIXED' || raw === 'FIXED_PRICE') return 'Flat';
  return formatTitleValue(value);
}

function formatTitleValue(value: unknown): string {
  return cleanString(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
